// Admin Voice Check helpers: parse the pasted names, list the clips to make,
// and build the CSV of results. Kept free of React so they can be unit-tested.

export const MAX_NAMES = 50;

export const VOICE_LABELS = { female: "Female", male: "Male" };

// Same rule as the server: letters (any alphabet, with accents), spaces,
// hyphens, apostrophes and dots, starting with a letter; at most 60 characters.
const NAME_RE = /^\p{L}[\p{L}\p{M} '’.-]*$/u;

/** One name per line: trimmed, inner spaces collapsed, duplicates (any case) dropped. */
export function parseNames(text, max = MAX_NAMES) {
  const seen = new Set();
  const names = [];
  const rejected = [];
  for (const raw of String(text || "").split(/\r?\n/)) {
    const name = raw.split(/\s+/).filter(Boolean).join(" ");
    if (!name) continue;
    if (name.length > 60 || !NAME_RE.test(name)) {
      rejected.push(name);
      continue;
    }
    const key = name.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return { names: names.slice(0, max), rejected, truncated: Math.max(0, names.length - max) };
}

/** The result columns: one per chosen interviewer voice (female, male). */
export function columnsFor(voices) {
  return voices.map((voice) => ({ key: voice, voice }));
}

/** Every clip to make, name by name, so results fill in row by row. */
export function buildJobs(names, columns) {
  return names.flatMap((name) => columns.map((c) => ({ key: `${name}|${c.key}`, name, voice: c.voice })));
}

/** Per column: how many marked ✓ (good) out of how many marked at all. */
export function summarize(columns, names, marks) {
  return columns.map((c) => {
    let good = 0;
    let marked = 0;
    for (const name of names) {
      const mark = marks[`${name}|${c.key}`];
      if (mark) marked += 1;
      if (mark === "good") good += 1;
    }
    return { ...c, good, marked };
  });
}

function csvCell(value) {
  const text = String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** name, voice, the interviewer voice used, the sentence spoken, and the mark. */
export function toCsv(jobs, results, marks) {
  const rows = [["name", "voice", "voice_name", "spoken_text", "mark"]];
  for (const job of jobs) {
    const mark = marks[job.key];
    rows.push([
      job.name,
      job.voice,
      results[job.key]?.voiceName || "",
      results[job.key]?.text || "",
      mark === "good" ? "good" : mark === "bad" ? "mispronounced" : "",
    ]);
  }
  return "﻿" + rows.map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
}
